import { memo, useState, useEffect } from "react";
import styles from "./OrderDetailsSideDialog.module.scss";
import CommonSideDialog from "../../../../../common/components/side-dialog/CommonSideDialog";
import { BounceLoader } from "react-spinners";
import { ChervonLeft, CircleCheckmark } from "../../../../../assets/icons";
import classNames from "classnames";
import { useGetCustomerOrderDetailsQuery, useLazyDownloadInvoiceQuery } from "../../../../../service/customer/customer-orders-service";
import moment from "moment";
import Slider, { Settings } from "react-slick";

type Props = {
	orderId: string;
	onCloseDialog: () => void;
};

const OrderDetailsSideDialog = memo((props: Props) => {
	const { onCloseDialog, orderId } = props;
	const [selectedImg, setSelectedImg] = useState(0);
	const [triggerDownload, setTriggerDownload] = useState(false);

	const { data, isLoading } = useGetCustomerOrderDetailsQuery({ orderId });
	const [downloadInvoice, { data: pdfBlob, isLoading: isDownloading }] = useLazyDownloadInvoiceQuery();

	const formatNumber = (num: number) => {
		const value = num.toFixed(2);
		return value.toString().replace(/\d(?=(\d{3})+\.)/g, "$&,");
	};

	const formatDuration = (duration: number) => {
		if (!duration) return "-";
		if (duration === 1) {
			return "1 HOUR ";
		} else {
			return `${duration} HOURS `;
		}
	};

	const formatPaymentPlanDuration = (durationOfPaymentsInMonths: number) => {
		if (!durationOfPaymentsInMonths) return "-";
		if (durationOfPaymentsInMonths === 1) {
			return "1 MONTH ";
		} else {
			return `${durationOfPaymentsInMonths} MONTHS`;
		}
	};

	const settings: Settings = {
		infinite: false,
		speed: 500,
		slidesToShow: 1,
		slidesToScroll: 1,
		arrows: true,
		beforeChange(_, nextSlide) {
			setSelectedImg(nextSlide);
		},
	};

	useEffect(() => {
		if (triggerDownload && !isDownloading && pdfBlob) {
			const url = URL.createObjectURL(pdfBlob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `Invoice_${data?.productName}_${moment().format("YYYYMMDD_HHmmss")}.pdf`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
			setTriggerDownload(false);
		}
	}, [pdfBlob, isDownloading, triggerDownload]);

	const handleDownload = () => {
		setTriggerDownload(true);
		downloadInvoice({ orderId });
	};

	return (
		<CommonSideDialog onCloseDialog={onCloseDialog}>
			<div className={styles.contentWrapper}>
				<div className={styles.header}>
					<button className={classNames("standardButton", styles.btn, styles.backBtn)} onClick={onCloseDialog}>
						<ChervonLeft /> Back
					</button>
					<button className={classNames("standardButton", styles.btn, styles.downloadBtn)} onClick={handleDownload}>
						{isDownloading ? "Downloading..." : "Download Invoice"} <ChervonLeft />
					</button>
				</div>
				{isLoading ? (
					<div className={styles.loader}>
						<BounceLoader size={100} color="#DE841B" />
					</div>
				) : (
					<div className={styles.content}>
						<h2>{data?.productName || "-"}</h2>
						<div className={styles.productInfo}>
							{data?.batteryType}
							<span className={styles.duration}>
								{formatDuration(data?.activeDuration || 0)}
								<span>{formatPaymentPlanDuration(data?.durationOfPaymentsInMonths || 0)}</span>
							</span>
						</div>

						{!!data?.images?.length && (
							<div className={styles.productImage}>
								<Slider
									{...settings}
									ref={slider => {
										slider?.slickGoTo(selectedImg);
									}}
								>
									{data?.images.map(image => (
										<div key={image.id} className={styles.image}>
											<img src={image?.imageUrl} alt="Image" />
										</div>
									))}
								</Slider>
							</div>
						)}

						<div className={styles.packageCost}>
							<span className={styles.label}>PACKAGE COST</span>
							<span className={styles.value}>₦{formatNumber(data?.totalPackageCost || 0)} </span>
						</div>
						<div className={styles.packageCostWrapper}>
							<div className={styles.packageCost}>
								<span className={classNames(styles.label, styles.paid)}>AMOUNT PAID</span>
								<span className={styles.value}>₦{formatNumber(data?.amountPaid || 0)}</span>
							</div>
							<div className={styles.packageCost}>
								<span className={classNames(styles.label, styles.left)}>AMOUNT LEFT</span>
								<span className={styles.value}>₦{formatNumber(data?.amountLeft || 0)}</span>
							</div>
						</div>

						<div className={styles.orderDetails}>
							<div className={styles.item}>
								<span className={styles.label}>Order Number</span>
								<span className={styles.value}>{data?.orderNumber}</span>
							</div>
							<div className={styles.item}>
								<span className={styles.label}>Delivery Address</span>
								<span className={styles.value}>{data?.deliveryAddress || "-"}</span>
							</div>
							<div className={styles.item}>
								<span className={styles.label}>Delivery Timeline</span>
								<span className={styles.value}>-</span>
							</div>
						</div>

						<div className={styles.orderStatus}>
							{data?.orderEvents?.map((event, index) => (
								<div className={styles.item} key={index}>
									<span className={styles.label}>
										<CircleCheckmark /> {event.name}
									</span>
									<span className={styles.value}>
										{moment(event.timestamp).format("h:mm A")} <span></span> {moment(event.timestamp).format("Do MMM. YYYY")}
									</span>
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		</CommonSideDialog>
	);
});

export default OrderDetailsSideDialog;
